import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, RefreshCw, DollarSign, Zap, Video } from 'lucide-react';
import { getAuthHeaders } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_TINT = {
  queued:    'bg-white/5 text-[#9CA3AF] border-white/10',
  in_prod:   'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  live:      'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30',
  reporting: 'bg-[#FF4343]/10 text-[#FF4343] border-[#FF4343]/30',
  complete:  'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30',
  cancelled: 'bg-white/5 text-[#6B7280] border-white/10',
};

const STATUSES = ['queued', 'in_prod', 'live', 'reporting', 'complete', 'cancelled'];

function OrderRow({ order, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(order.fulfillment_status || 'queued');
  const [asana, setAsana] = useState(order.asana_link || '');
  const [assignee, setAssignee] = useState(order.assignee || '');
  const [notes, setNotes] = useState(order.ops_notes || '');
  const [actualSpend, setActualSpend] = useState(order.actual_ad_spend_usd || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        fulfillment_status: status,
        asana_link: asana,
        assignee,
        ops_notes: notes,
      };
      if (order.order_type === 'amplify' && actualSpend !== '') {
        payload.actual_ad_spend_usd = parseFloat(actualSpend);
      }
      await axios.put(`${API}/admin/service-orders/${order.id}`, payload, { headers: getAuthHeaders() });
      toast.success('Order updated');
      setEditing(false);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#111827] border border-white/10 p-4" data-testid={`admin-order-${order.id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {order.order_type === 'amplify'
              ? <Zap className="w-4 h-4 text-[#FF4343]" />
              : <Video className="w-4 h-4 text-[#FF4343]" />}
            <span className="text-sm font-semibold text-white truncate">
              {order.channel_label || order.package_label}
            </span>
            <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${STATUS_TINT[order.fulfillment_status || 'queued']}`}>
              {(order.fulfillment_status || 'queued').replace('_', ' ')}
            </span>
            <span className={`text-[10px] font-mono uppercase ${order.payment_status === 'paid' ? 'text-[#10B981]' : 'text-yellow-400'}`}>
              {order.payment_status}
            </span>
          </div>
          <p className="text-xs text-[#9CA3AF]">
            {order.brand_name} · {order.brand_email}
          </p>
          <p className="text-[10px] text-[#6B7280] font-mono mt-0.5">
            {order.id} · {new Date(order.created_at).toLocaleString()}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-mono text-lg font-bold text-white">${(order.total_amount_usd || 0).toLocaleString()}</p>
          {order.order_type === 'amplify' && (
            <p className="text-[10px] text-[#6B7280]">
              spend ${(order.ad_spend_usd || 0).toLocaleString()} + base ${(order.base_fee_usd || 0).toLocaleString()} + 15% ${(order.mgmt_fee_usd || 0).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Extra context */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] mb-3 pb-3 border-b border-white/5">
        {order.linked_campaign_id && (
          <div><span className="text-[#6B7280]">Campaign</span><br /><span className="font-mono text-white">{order.linked_campaign_id.slice(0, 14)}…</span></div>
        )}
        {order.target_audience && (
          <div><span className="text-[#6B7280]">Audience</span><br /><span className="text-[#9CA3AF]">{order.target_audience}</span></div>
        )}
        {order.shoot_location && (
          <div><span className="text-[#6B7280]">Location</span><br /><span className="text-[#9CA3AF]">{order.shoot_location}</span></div>
        )}
        {order.preferred_dates && (
          <div><span className="text-[#6B7280]">Dates</span><br /><span className="text-[#9CA3AF]">{order.preferred_dates}</span></div>
        )}
        {order.notes && (
          <div className="col-span-2 md:col-span-4"><span className="text-[#6B7280]">Brand notes</span><br /><span className="text-[#9CA3AF]">{order.notes}</span></div>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                data-testid={`order-status-${order.id}`}
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm">
                {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Assignee</label>
              <input value={assignee} onChange={e => setAssignee(e.target.value)}
                placeholder="e.g. Jon (media buyer)"
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Asana task URL</label>
            <input value={asana} onChange={e => setAsana(e.target.value)}
              placeholder="https://app.asana.com/…"
              data-testid={`order-asana-${order.id}`}
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm" />
          </div>
          {order.order_type === 'amplify' && (
            <div>
              <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Actual ad spend (reporting)</label>
              <input type="number" value={actualSpend} onChange={e => setActualSpend(e.target.value)}
                placeholder={`${order.ad_spend_usd || 0}`}
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm font-mono" />
            </div>
          )}
          <div>
            <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Ops notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm resize-none" />
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving}
              data-testid={`order-save-${order.id}`}
              className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-4 py-2 text-xs font-semibold hover:bg-[#E03030] disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)}
              className="border border-white/20 text-white px-4 py-2 text-xs">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {order.asana_link && (
            <a href={order.asana_link} target="_blank" rel="noopener noreferrer"
              className="text-[11px] text-[#FF4343] hover:underline flex items-center gap-1">
              Asana <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {order.assignee && <span className="text-[11px] text-[#9CA3AF]">Assignee: {order.assignee}</span>}
          {order.ops_notes && <span className="text-[11px] text-[#6B7280] italic truncate max-w-xs">"{order.ops_notes}"</span>}
          <button onClick={() => setEditing(true)}
            data-testid={`order-edit-${order.id}`}
            className="ml-auto text-[11px] px-3 py-1 border border-white/20 text-white hover:bg-white/5">
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminServiceOrders() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ order_type: '', fulfillment_status: '', payment_status: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const [o, s] = await Promise.all([
        axios.get(`${API}/admin/service-orders`, { headers: getAuthHeaders(), params }),
        axios.get(`${API}/admin/service-orders/summary`, { headers: getAuthHeaders() }),
      ]);
      setOrders(o.data.orders || []);
      setSummary(s.data);
    } catch (e) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter.order_type, filter.fulfillment_status, filter.payment_status]);

  return (
    <div className="space-y-5" data-testid="admin-service-orders">
      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: 'Total revenue', value: `$${(summary.total_revenue_usd || 0).toLocaleString()}`, tint: 'text-[#FF4343]' },
            { label: 'Base fees', value: `$${(summary.total_base_fee_usd || 0).toLocaleString()}`, tint: 'text-[#10B981]' },
            { label: '15% mgmt fees', value: `$${(summary.total_mgmt_fee_usd || 0).toLocaleString()}`, tint: 'text-[#10B981]' },
            { label: 'Ad spend pool', value: `$${(summary.total_ad_spend_usd || 0).toLocaleString()}`, tint: 'text-white' },
            { label: 'Amplify paid', value: summary.amplify_paid, tint: 'text-white' },
            { label: 'Production paid', value: summary.production_paid, tint: 'text-white' },
          ].map(k => (
            <div key={k.label} className="bg-[#111827] border border-white/10 p-3">
              <p className="text-[9px] text-[#6B7280] font-mono uppercase truncate">{k.label}</p>
              <p className={`font-mono text-lg font-bold tabular-nums leading-tight truncate ${k.tint}`}>{k.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center bg-[#111827] border border-white/10 p-3">
        <select value={filter.order_type} onChange={e => setFilter({...filter, order_type: e.target.value})}
          data-testid="filter-order-type"
          className="px-3 py-1.5 bg-[#090E33] border border-white/10 text-white text-xs">
          <option value="">All types</option>
          <option value="amplify">Amplify (ads)</option>
          <option value="production">Production (video)</option>
        </select>
        <select value={filter.fulfillment_status} onChange={e => setFilter({...filter, fulfillment_status: e.target.value})}
          data-testid="filter-fulfillment"
          className="px-3 py-1.5 bg-[#090E33] border border-white/10 text-white text-xs">
          <option value="">Any fulfillment</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={filter.payment_status} onChange={e => setFilter({...filter, payment_status: e.target.value})}
          data-testid="filter-payment"
          className="px-3 py-1.5 bg-[#090E33] border border-white/10 text-white text-xs">
          <option value="">Any payment</option>
          <option value="paid">Paid</option>
          <option value="initiated">Initiated (pending)</option>
          <option value="expired">Expired</option>
        </select>
        <button onClick={load} data-testid="refresh-orders"
          className="ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5 border border-white/20 text-white hover:bg-white/5">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#9CA3AF]">Loading orders…</p>
      ) : orders.length === 0 ? (
        <div className="text-center py-10 bg-[#111827] border border-white/10">
          <DollarSign className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-sm text-[#9CA3AF]">No service orders match this filter.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map(o => <OrderRow key={o.id} order={o} onUpdate={load} />)}
        </div>
      )}
    </div>
  );
}
